'use client';

import { useCallback } from 'react';
import { UIMarket } from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';

/**
 * Hook for updating funding rates for perpetual markets
 */
export const useUpdateFundingRate = () => {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const updateFundingRate = useCallback(
		async (perpMarketIndex: number) => {
			if (!velocityClient || !walletAdapter) return false;
			const market = UIMarket.createPerpMarket(perpMarketIndex);

			const toastId = `updatingFundingRate_${perpMarketIndex}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				const txSig = await velocityClient.updateFundingRate(
					perpMarketIndex,
					market.market.oracle,
					getTxParams()
				);

				if (txSig) {
					notify({
						id: toastId,
						type: 'success',
						message: 'Updated.',
						showUntilCancelled: false,
					});
					return true;
				} else {
					notify({
						id: toastId,
						type: 'error',
						message: 'There was an error updating funding, please try again.',
						showUntilCancelled: false,
					});
					return false;
				}
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[velocityClient, walletAdapter, getTxParams]
	);

	return { updateFundingRate };
};
